import { Box, Stack, useTheme } from "@mui/material";
import { ScheduleCommonActions } from "../scheduleCommon/ScheduleCommonActions";
import { Win } from "../../../interfaces/dtos/win";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { suspendEP } from "../../../redux/win/winActions";
import { OptionProps, useAlerts, useFeedback } from "@sis/react-library";
import { winState } from "../../../redux/win/winSlice";
import { dispatchIsSuccessful, getEventDesc } from "../../../utils/commonUtils";
import { Button } from "../../shared/Button";
import { bettingPhases } from "../../../variables";
import { ActionBarSelectInput } from "../../shared/ActionBarSelectInput";
import { rdsEvent_Dto } from "../../../interfaces/dtos/rdsEvent";
import { rdsState } from "../../../redux/rds/rdsSlice";
import { Track } from "../../../interfaces/dtos/track";
import { getMarketStatusDisabledResult, getSuspendedDisabledResult } from "./scheduleWinActionsDisableRules";
import { ActionBarText } from "../../shared/ActionBarText";

interface ScheduleWinActionsLeftProps {
  currentEventWin?: Win;
  event?: rdsEvent_Dto;
  track?: Track;
  callUpdateBettingState: (newBettingPhase: string) => Promise<void>;
}

export const ScheduleWinActionsLeft = ({
  currentEventWin, event, track, callUpdateBettingState
}: ScheduleWinActionsLeftProps) => {

  const dispatch = useAppDispatch();
  const { confirm } = useAlerts();
  const { showSuccess } = useFeedback();
  const theme = useTheme();

  const win = useAppSelector(winState);
  const rds = useAppSelector(rdsState)
  const calc = currentEventWin?.calculated;

  const meeting = rds.rdsMeetings.find((m) => m.id === event?.meetingId);
  const eventStatus = rds.rdsEventStatuses.find((e) => e.id === event?.id);

  const marketStatusDisabledResult = getMarketStatusDisabledResult(win, currentEventWin, calc, track, meeting, eventStatus);
  const suspendedDisabledResult = getSuspendedDisabledResult(win, calc, meeting, eventStatus, track);

  const handleSuspendEP = async () => {
    const highlightProps: React.HtmlHTMLAttributes<HTMLSpanElement> = {
      style: { color: theme.palette.error.main, fontWeight: 'bold' }
    };
    const message = calc?.isSuspended && calc?.isAutoSuspended ?
      <>
        This market has "Betting Suspended" applied Automatically.<br />
        It may <span {...highlightProps}>not</span> be reformed correctly & could
        have <span {...highlightProps}>incorrect</span> prices for runners.<br /><br />
        Do you wish to continue?
      </> :
      <>
        Do you wish to <b>{calc?.isSuspended ? 'unsuspended' : 'suspended'}</b> this market?
      </>
    const title = `Confirm ${calc?.isSuspended ? 'unsuspend' : 'suspend'}`;
    confirm(title, message, async () => {
      await callSuspendApi();
    }, "Yes", "No");
  }

  const callSuspendApi = async () => {
    if (currentEventWin) {
      const response = await dispatch(suspendEP(
        {
          eventId: currentEventWin.eventId, suspend: !calc?.isSuspended,
          currentVersion: currentEventWin.version
        })
      )
      if (dispatchIsSuccessful(response)) {
        showSuccess(`Early Prices were successfully ${calc?.isSuspended ? 'unsuspended' : 'suspended'}`);
      }
    }
  }

  const getSuspendTooltip = () => {
    const text = `${calc?.isSuspended ? "Unsuspend" : "Suspend"} early prices`;
    if (suspendedDisabledResult.isDisabled) {
      return <>{text}<br />({suspendedDisabledResult.reason})</>
    } else {
      return text;
    }
  }

  const bettingPhaseOptions: OptionProps[] = [
    { value: bettingPhases.early, description: "Early Prices" },
    { value: bettingPhases.board, description: "Board Prices" }
  ];

  // allow parameter to be any
  // eslint-disable-next-line
  const handleBettingPhaseChange = (e: any) => {
    const newBettingPhase = e.target.value;
    const confirmMessage = <>
      You are sending <b>{newBettingPhase} prices</b> for<br /><b>{getEventDesc(event)}</b>.< br />< br />
      Do you wish to proceed?
    </>;
    confirm("Change betting phase", confirmMessage,
      () => {
        callUpdateBettingState(newBettingPhase);
      },
      "Yes", "No");
  }

  return (
    <Stack direction="row" spacing='10px' sx={{ height: '26px', marginTop: '-4px', alignItems: 'center' }}>
      <ScheduleCommonActions eventId={event?.id ?? 0} />
      {currentEventWin && !currentEventWin.calculated?.isIgnorePrice &&
        <>
          <Box>
            <Button
              id={`win-${calc?.isSuspended ? "unsuspend" : "suspend"}`}
              sx={{ textTransform: 'none', minWidth: '134px' }}
              onClick={handleSuspendEP}
              disabled={suspendedDisabledResult.isDisabled}
              busy={win.suspending}
              tooltip={getSuspendTooltip()}
            >
              {calc?.isSuspended ? "Unsuspend" : "Suspend"} EP
            </Button>
          </Box>
          <ActionBarSelectInput
            id="win-betting-phase"
            disabledCriteriaResult={marketStatusDisabledResult}
            value={currentEventWin.bettingState}
            options={bettingPhaseOptions}
            sx={{ backgroundColor: 'transparent' }}
            onChange={handleBettingPhaseChange}
          />
        </>
      }
      {currentEventWin && currentEventWin.calculated?.isIgnorePrice &&
        <ActionBarText sx={{ color: theme.palette.error.light, fontWeight: 'bold' }}>These prices are IGNORED</ActionBarText>
      }
    </Stack>
  )
}
